import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Terminal, Cpu, HardDrive, Clock, MapPin, 
  Server, ExternalLink, Activity, Info, StopCircle, Cloud, RefreshCw, Copy, Crosshair, Play, Trash2,
  Menu, Edit, Save
} from 'lucide-react';

const T = {
  bg: '#F9FAFB',
  panel: '#FFFFFF',
  textMain: '#111827',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  accent: '#4F46E5', // RunPod-like indigo/purple
  danger: '#EF4444',
  success: '#10B981',
  warningBg: '#FEF3C7',
  warningBorder: '#F59E0B',
  warningText: '#B45309'
};

const SectionCard = ({ title, children, noPadding = false }) => (
  <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', background: T.panel, marginBottom: '24px', overflow: 'hidden' }}>
    {title && (
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}`, background: '#F9FAFB', fontSize: '13px', fontWeight: 600, color: T.textMuted, display: 'flex', alignItems: 'center', gap: '8px' }}>
        {title}
      </div>
    )}
    <div style={{ padding: noPadding ? 0 : '16px' }}>
      {children}
    </div>
  </div>
);

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid #F3F4F6`, fontSize: '13px' }}>
    <span style={{ color: T.textMuted }}>{label}</span>
    <span style={{ color: T.textMain, fontWeight: 500 }}>{value}</span>
  </div>
);

const ThinGauge = ({ value, label, subtext, noBg = false }) => {
  const radius = 38;
  const strokeWidth = 14;
  const circumference = 2 * Math.PI * radius;
  const arcLength = circumference * 0.75;
  const gapLength = circumference * 0.25;
  
  // Safe value between 0-100
  const safeValue = Math.min(Math.max(value || 0, 0), 100);
  const filledLength = arcLength * (safeValue / 100);
  const emptyLength = circumference - filledLength;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: noBg ? 'none' : `1px solid ${T.border}`, borderRadius: '6px', padding: noBg ? '0' : '16px', background: noBg ? 'transparent' : T.panel }}>
      <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '24px', alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {label === 'VRAM' || label === 'Utilization' ? null : <Activity size={14} />} {label}
      </div>
      <div style={{ position: 'relative', width: '100px', height: '100px' }}>
        {/* Background Track Arc */}
        <svg style={{ position: 'absolute', top: 0, left: 0 }} width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="transparent" stroke="#E5E7EB" strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${arcLength} ${gapLength}`} transform="rotate(135 50 50)" />
        </svg>
        {/* Foreground Value Arc */}
        <svg style={{ position: 'absolute', top: 0, left: 0 }} width="100" height="100" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={radius} fill="transparent" stroke={T.accent} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={`${filledLength} ${emptyLength}`} transform="rotate(135 50 50)" />
        </svg>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '15px', color: T.accent }}>
          {safeValue}%
        </div>
      </div>
      {subtext && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '24px', textAlign: 'center', fontWeight: 500 }}>{subtext}</div>}
    </div>
  );
};

export default function PodDetailsModal({ pod, onClose }) {
  const [activeTab, setActiveTab] = React.useState(pod?.desiredStatus === 'RUNNING' ? 'connect' : 'details');
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [copied, setCopied] = React.useState('');
  const [isProcessing, setIsProcessing] = React.useState(false);

  const handlePodAction = async (actionType) => {
    if (actionType === 'edit' || actionType === 'save') {
      alert("This advanced feature is coming soon!");
      return;
    }
    if (actionType === 'sync') {
      alert("Syncing pod state...");
      setDropdownOpen(false);
      return;
    }

    try {
      setIsProcessing(true);
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://127.0.0.1:8787' : 'https://api.daggpt.network';
      
      const res = await fetch(`${API_URL}/api/compute/${actionType}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Assuming user auth headers are handled by a wrapper or we pass demo_user as seen in other components
          'x-user-id': 'demo_user' 
        },
        body: JSON.stringify({ podId: pod.id })
      });
      
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (data.errors) throw new Error(data.errors[0]?.message); // GraphQL errors
      
      alert(`Pod ${actionType} triggered successfully!`);
      setDropdownOpen(false);
    } catch (err) {
      console.error(err);
      alert(`Failed to ${actionType} pod: ${err.message}`);
    } finally {
      setIsProcessing(false);
    }
  };
  
  const runtime = pod?.runtime || {};
  const [liveUptime, setLiveUptime] = React.useState(runtime.uptimeInSeconds || 0);

  React.useEffect(() => {
    setLiveUptime(runtime.uptimeInSeconds || 0);
    const interval = setInterval(() => {
      setLiveUptime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [runtime.uptimeInSeconds]);

  if (!pod) return null;

  const gpus = runtime.gpus || [];
  const ports = runtime.ports || [];

  const formatUptime = (seconds) => {
    if (!seconds) return 'Just started';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <AnimatePresence>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', justifyContent: 'flex-end' }}>
        
        {/* Backdrop */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)' }}
        />

        {/* Side Panel (RunPod style) */}
        <motion.div 
          initial={{ x: '100%', boxShadow: '-5px 0 25px rgba(0,0,0,0)' }}
          animate={{ x: 0, boxShadow: '-5px 0 25px rgba(0,0,0,0.05)' }}
          exit={{ x: '100%', boxShadow: '-5px 0 25px rgba(0,0,0,0)' }}
          transition={{ type: 'tween', duration: 0.2 }}
          style={{ 
            position: 'relative', width: '100%', maxWidth: '850px', background: T.bg, 
            display: 'flex', flexDirection: 'column', height: '100vh', borderLeft: `1px solid ${T.border}`
          }}
        >
          {/* Header */}
          <div style={{ padding: '24px', background: T.panel, borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: T.textMuted, marginBottom: '8px' }}>
                  <span>Pods</span> <span style={{ color: '#D1D5DB' }}>›</span>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: pod.desiredStatus === 'RUNNING' ? T.success : '#9CA3AF' }} />
                  <span style={{ color: T.textMain, fontWeight: 600, fontSize: '16px' }}>{pod.name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: T.textMuted }}>
                    <span style={{ border: `1px solid ${T.border}`, padding: '2px 6px', borderRadius: '4px', background: '#F3F4F6' }}>ID</span>
                    <span style={{ fontFamily: 'monospace' }}>{pod.id}</span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textMuted }}>
                <X size={20} />
              </button>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px', position: 'relative' }}>
              
              {/* Dropdown Menu Button */}
              <button 
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={isProcessing}
                style={{ padding: '6px 8px', background: '#FFFFFF', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: '4px', display: 'flex', alignItems: 'center', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}
              >
                {isProcessing ? <RefreshCw size={16} className="spin" /> : <Menu size={16} />}
              </button>

              {/* Dropdown Content */}
              {dropdownOpen && !isProcessing && (
                <div style={{ position: 'absolute', top: '32px', left: 0, marginTop: '8px', background: T.panel, border: `1px solid ${T.border}`, borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', zIndex: 10, minWidth: '180px', overflow: 'hidden' }}>
                  <button onClick={() => handlePodAction('start')} style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: T.textMain }}>
                    <Play size={14} /> Start Pod
                  </button>
                  <button onClick={() => handlePodAction('edit')} style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: T.textMain }}>
                    <Edit size={14} /> Edit Pod
                  </button>
                  <button onClick={() => handlePodAction('save')} style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: T.textMain }}>
                    <Save size={14} /> Save as template
                  </button>
                  <button onClick={() => handlePodAction('sync')} style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: T.textMain }}>
                    <Cloud size={14} /> Sync Pod
                  </button>
                  <div style={{ height: '1px', background: T.border, margin: '4px 0' }} />
                  <button onClick={() => handlePodAction('terminate')} style={{ width: '100%', padding: '10px 16px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: T.danger }}>
                    <Trash2 size={14} /> Terminate Pod
                  </button>
                </div>
              )}

              {pod.desiredStatus !== 'RUNNING' ? (
                <>
                  <button onClick={() => handlePodAction('start')} disabled={isProcessing} style={{ padding: '6px 12px', background: T.accent, color: '#FFFFFF', border: 'none', borderRadius: '4px', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}>
                    <Play size={14} /> Start for ${pod.costPerHr || '0.20'}/hr
                  </button>
                  <button onClick={() => handlePodAction('terminate')} disabled={isProcessing} style={{ padding: '6px 12px', background: '#FEE2E2', color: T.danger, border: `1px solid #FCA5A5`, borderRadius: '4px', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}>
                    <Trash2 size={14} /> Terminate
                  </button>
                </>
              ) : (
                <button onClick={() => handlePodAction('stop')} disabled={isProcessing} style={{ padding: '6px 12px', background: '#FEE2E2', color: T.danger, border: `1px solid #FCA5A5`, borderRadius: '4px', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}>
                  <StopCircle size={14} /> Stop
                </button>
              )}
              <button onClick={() => handlePodAction('sync')} disabled={isProcessing} style={{ padding: '6px 12px', background: '#F3F4F6', color: T.textMain, border: `1px solid ${T.border}`, borderRadius: '4px', fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px', cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.6 : 1 }}>
                <Cloud size={14} /> Cloud Sync
              </button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div style={{ display: 'flex', padding: '0 24px', background: T.panel, borderBottom: `1px solid ${T.border}` }}>
            {(pod.desiredStatus === 'RUNNING' 
              ? ['Connect', 'Details', 'Telemetry'] 
              : ['Details', 'Template Details']
            ).map((tab) => (
              <button 
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase())}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: '12px 16px',
                  fontSize: '13px',
                  fontWeight: activeTab === tab.toLowerCase() ? 600 : 400,
                  color: activeTab === tab.toLowerCase() ? T.accent : T.textMuted,
                  cursor: 'pointer',
                  position: 'relative'
                }}
              >
                {tab}
                {activeTab === tab.toLowerCase() && (
                  <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: '2px', background: T.accent }} />
                )}
              </button>
            ))}
          </div>

          {/* Tab Content Area */}
          <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
            
            {/* CONNECT TAB */}
            {activeTab === 'connect' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '700px' }}>
                
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: T.textMain, margin: '0 0 8px' }}>HTTP services</h3>
                  <p style={{ fontSize: '13px', color: T.textMuted, margin: '0 0 16px' }}>Connect to your service using HTTP using a proxied domain and port</p>
                  
                  <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', background: T.panel, overflow: 'hidden' }}>
                    {ports.filter(p => p.type === 'http' || p.privatePort === 8888).length > 0 ? (
                      ports.filter(p => p.type === 'http' || p.privatePort === 8888).map((port, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: i === ports.length - 1 ? 'none' : `1px solid ${T.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                            <ExternalLink size={14} color={T.textMuted} />
                            <span style={{ color: T.textMain }}>Port {port.privatePort}</span>
                            <span style={{ color: T.textMuted }}>→</span>
                            <a href={`https://${pod.id}-${port.privatePort}.proxy.runpod.net`} target="_blank" rel="noreferrer" style={{ color: T.accent, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {port.privatePort === 8888 ? 'Jupyter notebook' : 'Web Interface'} <ExternalLink size={12} />
                            </a>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: T.accent }}>
                            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.accent }} />
                            Initializing...
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '16px', fontSize: '13px', color: T.textMuted }}>No HTTP services active.</div>
                    )}
                  </div>
                </div>

                <div style={{ background: T.warningBg, border: `1px solid ${T.warningBorder}`, padding: '12px 16px', borderRadius: '6px', fontSize: '13px', color: T.warningText, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={16} /> This is taking longer than expected. <a href="#" style={{ color: T.warningText, textDecoration: 'underline' }}>Learn more and troubleshoot <ExternalLink size={12} /></a>
                </div>

                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 600, color: T.textMain, margin: '0 0 8px' }}>Direct TCP ports</h3>
                  <p style={{ fontSize: '13px', color: T.textMuted, margin: '0 0 16px' }}>Connect to your Pod using direct TCP connections to exposed ports.</p>
                  
                  {ports.filter(p => p.type === 'tcp' || p.privatePort === 22).map((port, i) => {
                    const sshCommand = `ssh root@${port.ip} -p ${port.publicPort}`;
                    return (
                      <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: '6px', background: T.panel, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '13px', color: T.textMain }}>
                          <span style={{ color: T.textMuted }}>SSH →</span> {port.ip}:{port.publicPort} <span style={{ color: T.textMuted }}>→ {port.privatePort}</span>
                        </div>
                        <button 
                          onClick={() => handleCopy(sshCommand)}
                          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: T.textMuted }}
                          title="Copy SSH Command"
                        >
                          {copied === sshCommand ? <span style={{ fontSize: '12px', color: T.success }}>Copied!</span> : <Copy size={16} />}
                        </button>
                      </div>
                    );
                  })}
                </div>

              </div>
            )}

            {/* DETAILS TAB */}
            {activeTab === 'details' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  
                  <SectionCard title={<><Info size={14} /> Pod details</>}>
                    <DetailRow label="Uptime" value={formatUptime(liveUptime)} />
                    <DetailRow label="GPU" value={`${pod.name.split('(')[1]?.replace(')','') || 'RTX A4000'} x1`} />
                    <DetailRow label="vCPU" value={pod.vcpuCount ? `${pod.vcpuCount} Cores` : "18 (AMD EPYC 7702 64-Core Processor)"} />
                    <DetailRow label="Memory" value={pod.memoryInGb ? `${pod.memoryInGb} GB` : "71 GB"} />
                    <DetailRow label="Container disk" value={pod.containerDiskInGb ? `${pod.containerDiskInGb} GB` : "40 GB"} />
                  </SectionCard>

                  <SectionCard title={<><MapPin size={14} /> GPU location</>}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                      <span style={{ color: T.textMuted }}>Location</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 500 }}>EUR-IS-1</span>
                        <span style={{ padding: '2px 8px', border: `1px solid ${T.accent}`, borderRadius: '12px', color: T.accent, fontSize: '11px', fontWeight: 500 }}>Secure cloud</span>
                      </div>
                    </div>
                  </SectionCard>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  <SectionCard title={<>Pricing</>}>
                    <DetailRow label="Compute" value={`$${pod.costPerHr}/hr`} />
                    <DetailRow label="Container storage (40 GB)" value="$0.006/hr" />
                    <DetailRow label="Volume storage (20 GB)" value="$0.003/hr" />
                    <div style={{ borderTop: `1px solid ${T.border}`, margin: '8px 0' }} />
                    <DetailRow label="Total" value={`$${(parseFloat(pod.costPerHr) + 0.009).toFixed(3)}/hr`} />
                  </SectionCard>
                </div>
              </div>
            )}

            {/* TELEMETRY TAB */}
            {activeTab === 'telemetry' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', padding: '16px', background: T.panel }}>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><HardDrive size={12} /> Disk usage</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: T.textMain, fontWeight: 500, marginBottom: '8px' }}>
                    <span>0 Bytes (0%)</span>
                    <span style={{ color: T.textMuted }}>{pod.template?.containerDiskInGb || 40} GB</span>
                  </div>
                  <div style={{ height: '8px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: '1%', height: '100%', background: T.accent }} />
                  </div>
                </div>

                <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', padding: '16px', background: T.panel }}>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Server size={12} /> Volume usage</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: T.textMain, fontWeight: 500, marginBottom: '8px' }}>
                    <span>0 Bytes (0%)</span>
                    <span style={{ color: T.textMuted }}>20 GB</span>
                  </div>
                  <div style={{ height: '8px', background: '#F3F4F6', borderRadius: '4px', overflow: 'hidden', display: 'flex', gap: '2px' }}>
                    {Array.from({length: 20}).map((_, i) => (
                      <div key={i} style={{ flex: 1, background: '#E5E7EB', borderRadius: '2px' }} />
                    ))}
                  </div>
                </div>

                <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', padding: '16px', background: T.panel }}>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '8px' }}>
                    <Clock size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> Uptime
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: T.textMain }}>
                    {formatUptime(liveUptime)}
                  </div>
                </div>

                <div style={{ border: `1px solid ${T.border}`, borderRadius: '6px', padding: '16px', background: T.panel }}>
                  <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '8px' }}>
                    # Processes
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 600, color: T.textMain }}>
                    6
                  </div>
                </div>

                <ThinGauge label="CPU load" value={runtime.container?.cpuPercent || 0} subtext="AMD EPYC 7702" />
                <ThinGauge label="Memory" value={runtime.container?.memoryPercent || 0} subtext="4.55MB / 66.12GB" />

                {/* GPU Block */}
                {gpus.map((gpu, i) => (
                  <div key={i} style={{ gridColumn: '1 / -1', border: `1px solid ${T.border}`, borderRadius: '6px', background: T.panel, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${T.border}`, background: '#F9FAFB' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: T.textMuted }}>
                        <Crosshair size={14} /> {pod.machine?.gpuDisplayName || pod.name.split('(')[1]?.replace(')','') || 'RTX A4000'}
                      </div>
                      <div style={{ fontSize: '12px', color: T.textMuted, textAlign: 'right' }}>GPU<br/>{i}</div>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '20px', padding: '24px 20px' }}>
                      {/* VRAM Gauge */}
                      <ThinGauge label="VRAM" value={gpu.memoryUtilPercent} subtext="0 Bytes / 80 GiB" noBg={true} />
                      
                      {/* Utilization Gauge */}
                      <ThinGauge label="Utilization" value={gpu.gpuUtilPercent} subtext="27.0°C - 70.0W" noBg={true} />
                      
                      {/* Stats Column 1 */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', justifyContent: 'center', paddingLeft: '20px' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '6px' }}>Driver version</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: T.textMain }}>570.211.01</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '6px' }}>Power limit</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: T.textMain }}>700 W</div>
                        </div>
                      </div>

                      {/* Stats Column 2 */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', justifyContent: 'center' }}>
                        <div>
                          <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '6px' }}>CUDA version</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: T.textMain }}>12.8</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '12px', color: T.textMuted, marginBottom: '6px' }}>P-State</div>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: T.textMain }}>P0</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* TEMPLATE DETAILS TAB */}
            {activeTab === 'template details' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '700px' }}>
                
                <div style={{ paddingBottom: '16px', borderBottom: `1px solid ${T.border}` }}>
                  <DetailRow label="Template" value="Runpod Pytorch 2.1" />
                  <DetailRow label="Template ID" value="runpod-pytorch-21" />
                  <DetailRow label="Image" value={pod.template?.imageName || 'runpod/pytorch:2.1.0-py3.10-cuda11.8.0-devel-ubuntu22.04'} />
                  
                  <button style={{ marginTop: '12px', padding: '6px 12px', background: 'transparent', color: T.textMain, border: `1px solid ${T.border}`, borderRadius: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <Info size={14} /> Report problem with template
                  </button>
                </div>

                <div style={{ textAlign: 'center', color: '#D1D5DB', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px', position: 'relative' }}>
                  <span style={{ background: T.bg, padding: '0 8px', position: 'relative', zIndex: 1 }}>Template README</span>
                  <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', background: T.border, zIndex: 0 }} />
                </div>

                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: T.textMain, margin: '0 0 12px' }}>PyTorch Environment</h3>
                  <p style={{ fontSize: '13px', color: T.textMuted, margin: '0 0 16px' }}>Ready to use PyTorch + Python development environment with JupyterLab and common development tools pre-installed:</p>
                  
                  <ul style={{ paddingLeft: '20px', margin: '0 0 24px', fontSize: '13px', color: T.textMuted, lineHeight: '1.6' }}>
                    <li>Jupyter Notebook 6.5.4</li>
                    <li>JupyterLab with widgets and extensions</li>
                    <li>Pre-configured workspace directory</li>
                    <li>SSH access</li>
                    <li>NGINX server</li>
                    <li>Development tools</li>
                  </ul>
                </div>

                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, color: T.textMain, margin: '0 0 12px' }}>Ports</h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', border: `1px solid ${T.border}` }}>
                    <thead style={{ background: '#F9FAFB', borderBottom: `1px solid ${T.border}`, textAlign: 'left', color: T.textMuted }}>
                      <tr>
                        <th style={{ padding: '8px 16px', fontWeight: 500 }}>Application</th>
                        <th style={{ padding: '8px 16px', fontWeight: 500 }}>Port</th>
                        <th style={{ padding: '8px 16px', fontWeight: 500 }}>Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>Jupyter</td>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>8888</td>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>HTTP</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>SSH</td>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>22</td>
                        <td style={{ padding: '8px 16px', color: T.textMain }}>TCP</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
